import { Action } from '@reduxjs/toolkit';
import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { Input } from '@eika/input';
import { RadioButtonPanelOptions, RadioButtonPanels } from '@eika/radio-button';

import { formatShortDate } from '../../sporsmalsflyt/format';
import { SkadeWebDTO } from '../../types/generated-types';
import { setSkadesakExists } from '../reducer/ettersending-actions';
import { FormikProps } from './ettersending-form';
import { ettersendingSporsmal } from './ettersending-utils';

import './skade-selection.scss';

const otherSkadeValue = 'other_skade';

const SkadenummerInput: FC<FormikProps> = ({ formikValues }) => {
    const { values, errors, handleBlur, touched, handleChange } = formikValues;

    return (
        <div className="skade-selection">
            <Input
                value={values.skadenummerEllerForsikringsnummer || ''}
                name="skadenummerEllerForsikringsnummer"
                label={ettersendingSporsmal.skadenummerEllerForsikringsnummer}
                sublabel="Skadenummeret finner du på din forsikringssoversikt eller i e-poster fra oss om skaden. Gjelder det en personskade og du ikke har mottatt skadenummer ennå, kan du oppgi forsikringssnummeret ditt"
                type="numeric"
                onChange={handleChange}
                error={
                    touched.skadenummerEllerForsikringsnummer
                        ? (errors.skadenummerEllerForsikringsnummer as string)
                        : undefined
                }
                onBlur={handleBlur}
            />
        </div>
    );
};

interface SkadeSelectionProps extends FormikProps {
    skader: SkadeWebDTO[];
}

const SkadeSelection: FC<SkadeSelectionProps> = ({ skader, formikValues }) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const [annenSkadeSelected, setAnnenSkadeSelected] = useState<boolean>(false);
    const { values, errors, touched, setFieldValue } = formikValues;

    const getLabel = (skade: SkadeWebDTO): string =>
        skade.skadedato
            ? `${skade.skadetypeDescription} (${formatShortDate(skade.skadedato)}) - ${skade.produktnavn}`
            : `${skade.skadetypeDescription} - ${skade.produktnavn}`;

    const getOptions = (): RadioButtonPanelOptions[] => {
        const skadeOptions: RadioButtonPanelOptions[] = skader.map((skade) => ({
            value: skade.skadenummer.value,
            label: getLabel(skade),
            description: `Skadenummer ${skade.skadenummer.value}`,
        }));
        const otherSkadeOption: RadioButtonPanelOptions = {
            value: otherSkadeValue,
            label: `En annen skade`,
        };

        return [...skadeOptions, otherSkadeOption];
    };

    const onRadioButtonChange = (value: string) => {
        if (value === otherSkadeValue) {
            setFieldValue('skadenummerEllerForsikringsnummer', '');
        } else {
            setFieldValue('skadenummerEllerForsikringsnummer', value);
        }
        setAnnenSkadeSelected(value === otherSkadeValue);
        dispatch(setSkadesakExists(value !== otherSkadeValue));
    };

    if (skader.length < 1) return <SkadenummerInput formikValues={formikValues} />;

    return (
        <>
            <div className="skade-selection">
                <RadioButtonPanels
                    label="Hvilken skade gjelder det?"
                    errorMessage={
                        touched.skadenummerEllerForsikringsnummer && !annenSkadeSelected
                            ? errors.skadenummerEllerForsikringsnummer
                            : undefined
                    }
                    onChange={onRadioButtonChange}
                    options={getOptions()}
                    selectedOption={annenSkadeSelected ? otherSkadeValue : values.skadenummerEllerForsikringsnummer}
                />
            </div>
            {annenSkadeSelected && <SkadenummerInput formikValues={formikValues} />}
        </>
    );
};

export default SkadeSelection;
